import { useEffect, useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { SectionTitle } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none transition-colors focus:border-clay";

const LABELS = {
  invoices: "invoices",
  payments: "payment records",
  refunds: "refunds",
  invoice_reminders: "reminders sent",
  reminder_failures: "reminder retries",
  cron_runs: "reminder runs",
};

/** Removes sample billing data after go-live. Square's own transaction history is untouched. */
export default function BillingDataReset() {
  const [summary, setSummary] = useState(null);
  const [phrase, setPhrase] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/billing-data").then((r) => setSummary(r.data)).catch(() => setSummary(null));

  useEffect(() => { load(); }, []);

  if (!summary) return null;

  const clear = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/billing-data/clear", { confirm: phrase });
      const total = Object.values(data.removed).reduce((a, b) => a + b, 0);
      toast.success(`Removed ${total} billing records`);
      setPhrase("");
      setOpen(false);
      load();
      window.dispatchEvent(new Event("billing-data-cleared"));
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const items = Object.entries(summary.counts).filter(([, n]) => n > 0);

  return (
    <div className="card-surface border-l-2 border-l-clay p-7" data-testid="billing-data-reset">
      <SectionTitle>Clear Billing Data</SectionTitle>
      <p className="-mt-3 max-w-2xl text-sm text-stone">
        Removes every invoice, payment record and reminder stored in this environment — useful for
        clearing sample data before real clients arrive. Your Square account history is not affected,
        and projects, clients, products and schedules are left untouched.
      </p>

      <div className="mt-6 text-sm text-ink" data-testid="billing-data-counts">
        {items.length === 0 ? (
          <span className="text-fog">No billing records stored.</span>
        ) : (
          items.map(([key, n]) => (
            <span key={key} className="mr-5 inline-block">
              <span className="num">{n}</span> <span className="text-stone">{LABELS[key] || key}</span>
            </span>
          ))
        )}
      </div>

      {items.length > 0 && (
        !open ? (
          <button data-testid="billing-data-reset-open" onClick={() => setOpen(true)}
            className="mt-7 inline-flex items-center gap-2 rounded-sm border border-clay bg-white px-4 py-2.5 text-sm text-clay transition-colors hover:bg-[#F6DED6]">
            <Trash2 className="h-4 w-4" strokeWidth={1.5} /> Clear billing data
          </button>
        ) : (
          <div className="mt-7 border-t border-line pt-6">
            <p className="flex flex-wrap items-start gap-x-1.5 gap-y-1 text-sm text-[#A8492E]">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
              <span>This cannot be undone.</span>
              <span className="whitespace-nowrap">Type <span className="num">{summary.confirm_phrase}</span> to confirm.</span>
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <input data-testid="billing-data-confirm-input" value={phrase}
                onChange={(e) => setPhrase(e.target.value)} placeholder={summary.confirm_phrase}
                className={`num max-w-xs ${input}`} />
              <button data-testid="billing-data-confirm-btn" onClick={clear}
                disabled={busy || phrase.trim() !== summary.confirm_phrase}
                className="rounded-sm bg-clay px-4 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40">
                {busy ? "Clearing…" : "Clear permanently"}
              </button>
              <button data-testid="billing-data-cancel-btn" onClick={() => { setOpen(false); setPhrase(""); }}
                className="text-sm text-stone hover:text-ink">Cancel</button>
            </div>
          </div>
        )
      )}
    </div>
  );
}
